import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import { disconnectTeamsUser, getTeamsChannels, getTeamsStatus, uninstallTeams } from './teams-integrations-client';
import type { UseTeamsChannelsResult, UseTeamsStatusResult } from './teams-integrations-client.type';

const TEAMS_QUERY_KEY = 'teams-integration';

/**
 * Hook to fetch Teams install + per-user-connect status for the current org.
 *
 * Mirrors `useSlackStatus`: overrides the app's global staleTime defaults so
 * the post-OAuth redirect lands users back on the integrations page with an
 * immediate state change rather than stale cache. Gating is delegated entirely
 * to the caller via `enabled` (the `FloraTeamsIntegration` LaunchDarkly flag) —
 * the hook does not hard-gate on the build environment, so the query stays
 * paused (default `false`) until a caller opts in with the flag.
 *
 * @param {boolean} enabled - When false, the query is paused.
 * @returns {UseTeamsStatusResult} Status object plus the underlying React Query result.
 */
function useTeamsStatus(enabled = false): UseTeamsStatusResult {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [TEAMS_QUERY_KEY, 'status', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getTeamsStatus(organizationId);
    },
    enabled: !!organizationId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return { data: query.data, query };
}

/**
 * Hook to fetch the Teams channel list for the channel-picker UX.
 *
 * Returns 409 from the BE when the user has not completed OAuth — the consumer
 * should handle that as a "connect first" prompt rather than an error.
 *
 * @param {boolean} enabled - When false, the query is paused (e.g. user not connected yet).
 * @returns {UseTeamsChannelsResult} Channels list plus the underlying React Query result.
 */
function useTeamsChannels(enabled = true): UseTeamsChannelsResult {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [TEAMS_QUERY_KEY, 'channels', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getTeamsChannels(organizationId);
    },
    enabled: !!organizationId && enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return { data: query.data?.channels, query };
}

/**
 * Hook to uninstall the org's Teams install (admin only).
 *
 * Invalidates the status query on success so the page reflects the new state.
 *
 * @returns {UseMutationResult<void, Error, void>} Mutation result.
 */
function useUninstallTeams(): UseMutationResult<void, Error, void> {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required to uninstall Teams');
      }
      return uninstallTeams(organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to disconnect the current user's Teams account from the org.
 *
 * @returns {UseMutationResult<void, Error, void>} Mutation result.
 */
function useDisconnectTeamsUser(): UseMutationResult<void, Error, void> {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required to disconnect Teams account');
      }
      return disconnectTeamsUser(organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_QUERY_KEY] });
    },
  });
}

export { useDisconnectTeamsUser, useTeamsChannels, useTeamsStatus, useUninstallTeams };
